import { Controller } from 'react-hook-form';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { DatePicker } from '@/shared/components/ui/DatePicker';
import { parseApiDate, toApiDate } from '@/shared/utils/dateUtils';
import { cn } from '@/core/lib/utils';
import { ConvertToTaskField } from './ConvertToTaskField';

interface ConvertToTaskDateFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  placeholder: string;
  aiLabel?: string;
  /** Muted note under the control, e.g. wording the API could not resolve. */
  hint?: string;
  disabled?: boolean;
  /** Blue outline the design gives every AI-filled control. */
  highlighted?: boolean;
  'data-testid'?: string;
}

/**
 * A labelled date row. The form holds a `Date`; `DatePicker` speaks the API's
 * UTC ISO string, so the conversion lives here instead of in every caller.
 */
export function ConvertToTaskDateField<T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  aiLabel,
  hint,
  disabled,
  highlighted,
  'data-testid': testId,
}: ConvertToTaskDateFieldProps<T>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <ConvertToTaskField label={label} aiLabel={aiLabel} hint={hint} error={fieldState.error}>
          <DatePicker
            value={toApiDate(field.value)}
            onChange={(value) => field.onChange(parseApiDate(value))}
            placeholder={placeholder}
            disabled={disabled}
            className={cn('h-10', highlighted && 'border-primary')}
            data-testid={testId}
          />
        </ConvertToTaskField>
      )}
    />
  );
}
